import type {
  EnrollmentSessionFieldEntry,
  EnrollmentSessionFieldValue,
  EnrollmentStepSubmissionResponse,
  EnrollmentSummary,
  EnrollmentUserProfile,
  ResumedEnrollment
} from '@/api/enrollment-api'
import { normalizeStateName } from '../../components/eligibilitySteps/states'
import type { EnrollmentFieldEditability, HydratableEnrollment } from './types'

const ACTIVE_STATUSES = new Set([
  'STARTED',
  'ACCOUNT_CREATED',
  'ACCOUNT_VERIFIED',
  'ELIGIBILITY_COMPLETED',
  'LICENSE_INFO_COMPLETED',
  'PERSONAL_INFO_COMPLETED',
  'LOCATION_SELECTED',
  'PROGRAM_SELECTED',
  'PREFERENCES_COMPLETED',
  'START_DATE_SELECTED',
  'TUITION_REVIEWED',
  'ASSESSMENT_PENDING',
  'ASSESSMENT_PASSED',
  'DOCUMENTS_PENDING',
  'DOCUMENTS_UPLOADED',
  'SIGNING_PENDING',
  'ENROLLMENT_DOCS_SIGNED',
  'SUBMITTED',
  'APPROVED',
  'ENROLLED'
])

type EnrollmentFieldsRecord = Record<string, EnrollmentSessionFieldValue | null>
type EnrollmentFields = EnrollmentFieldsRecord | EnrollmentSessionFieldEntry[]

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

export const prefer = (next: unknown, fallback: string) =>
  isNonEmptyString(next) ? next.trim() : fallback

export const preferBoolean = (next: unknown, fallback: boolean | null) =>
  typeof next === 'boolean' ? next : fallback

export const nonEmptyArray = (next: unknown) => {
  const values = Array.isArray(next) ? next.filter(isNonEmptyString) : []
  return values.length > 0 ? values : null
}

export const formatSsnLastFour = (value: string | null | undefined) => {
  const digits = value?.replace(/\D/g, '') ?? ''
  return digits.length === 4 ? `xxx-xx-${digits}` : ''
}

export const getMostRelevantEnrollment = (
  profile?: EnrollmentUserProfile | null
): EnrollmentSummary | null => {
  if (!profile) return null
  if (profile.currentEnrollment) return profile.currentEnrollment

  const enrollments = profile.enrollments ?? []
  return (
    [...enrollments]
      .reverse()
      .find(enrollment => !enrollment.status || ACTIVE_STATUSES.has(enrollment.status)) ??
    enrollments[enrollments.length - 1] ??
    null
  )
}

export const getDriverLicenseDocumentFileName = (enrollment: HydratableEnrollment) => {
  const direct = (enrollment as Record<string, unknown> | null | undefined)
    ?.driverLicenseDocumentFileName
  if (isNonEmptyString(direct)) return direct

  const documents = (
    enrollment as ResumedEnrollment | EnrollmentStepSubmissionResponse | null | undefined
  )?.documents
  const driverLicense = documents?.find(document => document.documentType === 'DRIVERS_LICENSE')
  return driverLicense?.fileName ?? ''
}

const isEnrollmentFieldsRecord = (value: unknown): value is EnrollmentFieldsRecord => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const getEnrollmentFields = (enrollment: HydratableEnrollment): EnrollmentFields | null => {
  const fields = (
    enrollment as
      | {
          fields?: EnrollmentFields | null
        }
      | null
      | undefined
  )?.fields

  if (Array.isArray(fields) || isEnrollmentFieldsRecord(fields)) {
    return fields
  }

  return null
}

const getFieldEntryValue = (entry: EnrollmentSessionFieldValue | null | undefined) => entry?.value

const getFieldValueFromArray = (fields: EnrollmentSessionFieldEntry[], key: string) =>
  getFieldEntryValue(
    fields.find(entry => isNonEmptyString(entry.key) && entry.key.trim() === key)
  )

const getFieldValueFromRecord = (fields: EnrollmentFieldsRecord, key: string) =>
  getFieldEntryValue(fields[key])

const getFieldValue = (enrollment: HydratableEnrollment, keys: string[]) => {
  const fields = getEnrollmentFields(enrollment)
  if (!fields) {
    return undefined
  }

  for (const key of keys) {
    const value = Array.isArray(fields)
      ? getFieldValueFromArray(fields, key)
      : getFieldValueFromRecord(fields, key)

    if (value !== undefined && value !== null) {
      return value
    }
  }

  return undefined
}

export const getEnrollmentString = (enrollment: HydratableEnrollment, keys: string[]) => {
  const record = enrollment as Record<string, unknown> | null | undefined
  for (const key of keys) {
    const value = record?.[key]
    if (isNonEmptyString(value)) return value
  }

  const fieldValue = getFieldValue(enrollment, keys)
  if (isNonEmptyString(fieldValue)) {
    return fieldValue
  }

  return ''
}

export const getEnrollmentBoolean = (enrollment: HydratableEnrollment, keys: string[]) => {
  const record = enrollment as Record<string, unknown> | null | undefined
  for (const key of keys) {
    const value = record?.[key]
    if (typeof value === 'boolean') return value
  }

  const fieldValue = getFieldValue(enrollment, keys)
  if (typeof fieldValue === 'boolean') {
    return fieldValue
  }
  if (typeof fieldValue === 'string') {
    const normalizedValue = fieldValue.trim().toLowerCase()
    if (normalizedValue === 'true') return true
    if (normalizedValue === 'false') return false
  }

  return null
}

export const getEnrollmentNumber = (enrollment: HydratableEnrollment, keys: string[]) => {
  const record = enrollment as Record<string, unknown> | null | undefined
  for (const key of keys) {
    const value = record?.[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
  }

  const fieldValue = getFieldValue(enrollment, keys)
  if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
    return fieldValue
  }
  if (typeof fieldValue === 'string') {
    const parsedValue = Number(fieldValue)
    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }
  }

  return null
}

export const getEnrollmentStringArray = (enrollment: HydratableEnrollment, keys: string[]) => {
  const record = enrollment as Record<string, unknown> | null | undefined
  for (const key of keys) {
    const value = record?.[key]
    const values = Array.isArray(value) ? value.filter(isNonEmptyString) : []
    if (values.length > 0) return values
  }

  const fieldValue = getFieldValue(enrollment, keys)
  if (Array.isArray(fieldValue)) {
    const values = fieldValue.filter(isNonEmptyString)
    if (values.length > 0) {
      return values
    }
  }

  return null
}

export const getEnrollmentFieldEditability = (
  enrollment: HydratableEnrollment
): EnrollmentFieldEditability => {
  const fields = getEnrollmentFields(enrollment)
  if (!fields) {
    return {}
  }

  if (Array.isArray(fields)) {
    return fields.reduce<EnrollmentFieldEditability>((result, entry) => {
      if (!isNonEmptyString(entry.key)) {
        return result
      }

      result[entry.key.trim()] = entry.editable !== false
      return result
    }, {})
  }

  return Object.entries(fields).reduce<EnrollmentFieldEditability>((result, [key, entry]) => {
    result[key] = entry?.editable !== false
    return result
  }, {})
}

export { normalizeStateName }
